/**
 * One-line case summaries for list surfaces (Home, the Cases page).
 * Part of the 2026-08-27 case-first redesign, kept here so the two
 * pages can't drift apart on how a case describes itself.
 */

import type { Bill } from '../types/bill';
import { STATUS_CONFIG } from '../types/bill';
import { CASE_OUTCOME_LABELS } from './case-state';

/* A name that came straight off an upload: "Screenshot 2026-09-17 at
   10.44.34.png", "1787600630859-Medical Bill (1).pdf". We keep it on the
   row so ops can tell which file is which, but it is a poor name for a
   case, so the portal doesn't show it. Anything a person typed (or the
   provider name the engine found) is used as-is. */
const FILE_NAME = /\.(pdf|png|jpe?g|heic|heif|webp|gif|tiff?)$/i;
const UPLOAD_PREFIX = /^\d{10,}[-_]/;

export function looksLikeFileName(name: string | null | undefined): boolean {
  if (!name) return false;
  const n = name.trim();
  return FILE_NAME.test(n) || UPLOAD_PREFIX.test(n);
}

/** What to call a case anywhere the customer can see it. */
export function caseTitle(
  bill: { display_name?: string | null; provider_name?: string | null },
  fallback = 'Medical bill',
): string {
  const given = bill.display_name?.trim();
  if (given && !looksLikeFileName(given)) return given;
  const provider = bill.provider_name?.trim();
  if (provider) return provider;
  return fallback;
}

/* List-level "closed" matches the Cases page filter: resolved v2 cases,
   and the three legacy terminal statuses for pre-084 rows. */
export function isBillClosed(bill: Bill): boolean {
  return bill.case_stage
    ? bill.case_stage === 'resolved'
    : ['completed', 'no_savings', 'canceled'].includes(bill.status);
}

const WAITING_PHRASE: Record<string, string> = {
  mediloop: 'our team',
  customer: 'you',
  provider: 'the provider',
  insurer: 'the insurer',
  collections_agency: 'the collections agency',
};

export function caseStatusLine(bill: Bill): string {
  if (isBillClosed(bill)) {
    if (bill.case_stage === 'resolved') {
      const o = bill.case_outcome;
      if (!o) return 'Resolved';
      if (o === 'customer_withdrew') return 'Closed at your request';
      if (o === 'customer_unresponsive') return 'Closed, we didn’t hear back';
      const label = CASE_OUTCOME_LABELS[o];
      return `Resolved · ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
    }
    return STATUS_CONFIG[bill.status]?.label || 'Closed';
  }
  if (bill.paused_at) {
    return bill.pause_reason === 'customer_unresponsive'
      ? 'Paused until we hear from you'
      : 'Paused';
  }
  if (bill.case_stage) {
    const waiting =
      bill.waiting_on && bill.waiting_on !== 'none'
        ? WAITING_PHRASE[bill.waiting_on] ?? null
        : null;
    const next = bill.next_customer_update_at
      ? new Date(bill.next_customer_update_at)
      : null;
    const nextLabel =
      next && !Number.isNaN(next.getTime())
        ? next.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        : null;
    let line = 'In progress';
    if (waiting) line += ` · waiting on ${waiting}`;
    if (nextLabel) line += ` · update by ${nextLabel}`;
    return line;
  }
  return STATUS_CONFIG[bill.status]?.label || 'In progress';
}

export type CasePillTone = 'neutral' | 'you' | 'done';

/* The one-word status pill on Home and Cases (Oct 2026 revamp). Only two
   states get colour: something the customer has to do, and a closed case
   with money saved. Everything else is a quiet "In progress". */
export function casePill(bill: Bill): { label: string; tone: CasePillTone } {
  if (isBillClosed(bill)) {
    const saved = Number(bill.amount_saved) || 0;
    return saved > 0
      ? { label: `Saved $${saved.toLocaleString()}`, tone: 'done' }
      : { label: 'Closed', tone: 'neutral' };
  }
  const needsYou =
    bill.waiting_on === 'customer' ||
    (bill.paused_at && bill.pause_reason === 'customer_unresponsive') ||
    bill.status === 'info_needed' ||
    bill.status === 'no_response';
  if (needsYou) return { label: 'Needs you', tone: 'you' };
  return { label: 'In progress', tone: 'neutral' };
}
